import { useEffect, useCallback } from 'react';
import { DEFAULT_STATUSES } from '../FlowchartView/TaskNode/constants';
import { mergeWithDefaults } from '../config/cardDisplayConfig';
import { DEFAULT_SWIMLANE_SETTING } from '../config/swimlaneConfig';

const DEFAULT_SORT_SETTING = 'manual';

/**
 * useSprintDetailSetup Hook
 * 
 * Handles derived data, effects, and settings handlers for SprintDetail.
 * Extracts setup logic to reduce main component size.
 */
const useSprintDetailSetup = ({
  localSprint,
  setLocalSprint,
  actions,
  onSprintIconChange,
  setFiltersPerView,
  setShowArchived,
  setShowActionableOnly,
  setKanbanSelectedIds,
  setTableSelectedIds,
  settings = {},
}) => {
  // Derived data
  const tasks = localSprint?.tasks || [];
  const statuses = localSprint?.statuses || DEFAULT_STATUSES;
  const cardDisplaySettingsPerView = mergeWithDefaults(localSprint?.cardDisplaySettings);
  const swimlaneSetting = localSprint?.swimlaneSetting || DEFAULT_SWIMLANE_SETTING;
  const sortSettingsPerView = localSprint?.sortSettingsPerView || {
    flow: DEFAULT_SORT_SETTING,
    kanban: DEFAULT_SORT_SETTING,
    table: DEFAULT_SORT_SETTING,
    gantt: DEFAULT_SORT_SETTING,
    todolist: DEFAULT_SORT_SETTING,
    neural: DEFAULT_SORT_SETTING,
  };
  const allTags = actions.getAllTags?.() || [];
  const archivedCount = tasks.filter(task => task.archived).length;

  // Initialize statuses if campaign doesn't have them yet
  useEffect(() => {
    if (localSprint && !localSprint.statuses) {
      setLocalSprint(prev => {
        if (!prev || prev.statuses) return prev;
        return { ...prev, statuses: DEFAULT_STATUSES };
      });
    }
  }, [localSprint?.id, localSprint?.statuses, setLocalSprint]);

  // Auto-archive completed tasks with no activity
  // Runs once when campaign loads (when localSprint.id changes)
  // Respects user settings for enabled/disabled and days threshold
  useEffect(() => {
    const autoArchiveEnabled = settings.autoArchiveEnabled ?? true;
    const autoArchiveDays = settings.autoArchiveDays ?? 7;
    
    if (localSprint?.id && actions?.handleAutoArchiveCompletedTasks && autoArchiveEnabled) {
      // Small delay to ensure sprint data is fully loaded
      const timer = setTimeout(() => {
        actions.handleAutoArchiveCompletedTasks(autoArchiveDays);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [localSprint?.id, actions, settings.autoArchiveEnabled, settings.autoArchiveDays]);

  // Listen for selection change events from views
  useEffect(() => {
    const handleKanbanSelection = (e) => {
      console.log('[SprintDetail] Kanban selection event received', e.detail);
      setKanbanSelectedIds(e.detail.selectedTaskIds || new Set());
    };
    const handleTableSelection = (e) => {
      console.log('[SprintDetail] Table selection event received', e.detail);
      setTableSelectedIds(e.detail.selectedTaskIds || new Set());
    };
    
    window.addEventListener('kanbanSelectionChange', handleKanbanSelection);
    window.addEventListener('tableSelectionChange', handleTableSelection);
    
    return () => {
      window.removeEventListener('kanbanSelectionChange', handleKanbanSelection);
      window.removeEventListener('tableSelectionChange', handleTableSelection);
    };
  }, [setKanbanSelectedIds, setTableSelectedIds]);

  // Settings handlers
  const handleCardDisplaySettingsChange = useCallback((view, newSettings) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentPerView = mergeWithDefaults(prev.cardDisplaySettings);
      return { ...prev, cardDisplaySettings: { ...currentPerView, [view]: newSettings } };
    });
  }, [setLocalSprint]);

  const handleSwimlaneSettingChange = useCallback((newSetting) => {
    setLocalSprint(prev => prev ? { ...prev, swimlaneSetting: newSetting } : prev);
  }, [setLocalSprint]);

  const handleSortSettingChange = useCallback((view, newSetting) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const currentPerView = prev.sortSettingsPerView || {
        flow: DEFAULT_SORT_SETTING,
        kanban: DEFAULT_SORT_SETTING,
        table: DEFAULT_SORT_SETTING,
        gantt: DEFAULT_SORT_SETTING,
        todolist: DEFAULT_SORT_SETTING,
        neural: DEFAULT_SORT_SETTING,
      };
      return { ...prev, sortSettingsPerView: { ...currentPerView, [view]: newSetting } };
    });
  }, [setLocalSprint]);

  const handleFiltersChange = useCallback((view, newFilters) => {
    setFiltersPerView(prev => ({ ...prev, [view]: newFilters }));
  }, [setFiltersPerView]);

  const handleToggleArchived = useCallback(() => setShowArchived(prev => !prev), [setShowArchived]);
  const handleToggleActionableOnly = useCallback(() => setShowActionableOnly(prev => !prev), [setShowActionableOnly]);

  const handleLocalSprintIconChange = useCallback((newIcon) => {
    if (onSprintIconChange && localSprint?.id) {
      onSprintIconChange(localSprint.id, newIcon);
    }
  }, [onSprintIconChange, localSprint?.id]);

  return {
    // Derived data
    tasks,
    statuses,
    cardDisplaySettingsPerView,
    swimlaneSetting,
    sortSettingsPerView,
    allTags,
    archivedCount,
    
    // Settings handlers
    handleCardDisplaySettingsChange,
    handleSwimlaneSettingChange,
    handleSortSettingChange,
    handleFiltersChange,
    handleToggleArchived,
    handleToggleActionableOnly,
    handleLocalSprintIconChange,
  };
};

export default useSprintDetailSetup;
